import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  EmailAuthProvider, GoogleAuthProvider,
  reauthenticateWithCredential, reauthenticateWithPopup,
} from "firebase/auth";
import logo from "../../images/logo.svg";
import { useUser } from "./UserContext";
import { useClassroomMode } from "./classroomMode";
import "./ClassroomNavBar.css";

const tabClass = ({ isActive }) => `cn-tab ${isActive ? "active" : ""}`;

// Inline so it inherits currentColor (matches the navy tab color).
const HamburgerIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" width="24" height="24">
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

// Minimal nav for Classroom Mode: only the kid-safe surfaces + a lock to exit.
// No Settings, no Check List/Emergency, no account Log Out. On desktop the tabs
// sit centered; on phones they collapse into a tiny hamburger menu (the flat row
// used to overflow/mis-align on small screens).
export default function ClassroomNavBar() {
  const { libraryEnabled, lendingTrackers, classroomPin, user } = useUser();
  const { exitClassroomMode } = useClassroomMode();
  const navigate = useNavigate();
  const [showExit, setShowExit] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  // "Forgot PIN?" escape hatch — proves it's the account owner (a kid can't) so a
  // forgotten PIN never traps the teacher in the locked view. Logging out doesn't
  // help (the mode is a device localStorage flag that re-locks on next login), so
  // recovery must re-authenticate the Firebase account, then clear the flag.
  const [forgot, setForgot] = useState(false);
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const providers = (user?.providerData || []).map((p) => p.providerId);
  const hasPassword = providers.includes("password");

  // The kid-safe destinations, gated the same way desktop + mobile.
  const links = [
    { to: "/classsignoutpage", label: "Sign Out" },
    { to: "/attendance", label: "Attendance" },
    libraryEnabled && { to: "/library", label: "Library" },
    lendingTrackers.length > 0 && { to: "/lending", label: "Lending" },
  ].filter(Boolean);

  const closeMenu = () => setMenuOpen(false);
  const closeModal = () => {
    setShowExit(false); setPin(""); setError("");
    setForgot(false); setPw(""); setBusy(false);
  };

  const openExit = () => { setMenuOpen(false); setShowExit(true); };

  const tryExit = () => {
    // No PIN on file → allow exit (never lock the teacher out of their own device).
    if (!classroomPin || pin === classroomPin) {
      closeModal();
      exitClassroomMode();
    } else {
      setError("Incorrect PIN.");
      setPin("");
    }
  };

  // Recover from a forgotten PIN by re-authenticating the signed-in teacher, then
  // exit Classroom Mode. Password accounts re-enter their password; Google accounts
  // re-verify via the Google popup. On success we drop the device flag so they land
  // back in the full app (they can set a fresh PIN in Settings → Classroom).
  const recoverAndExit = async () => {
    if (!user) return;
    setBusy(true); setError("");
    try {
      if (hasPassword) {
        if (!pw) { setError("Enter your account password."); setBusy(false); return; }
        await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, pw));
      } else {
        await reauthenticateWithPopup(user, new GoogleAuthProvider());
      }
      closeModal();
      exitClassroomMode();
    } catch (e) {
      setError(hasPassword ? "That password didn't match." : "Couldn't verify — please try again.");
      setPw("");
      setBusy(false);
    }
  };

  return (
    <div className="classroom-navbar">
      <img
        src={logo}
        className="cn-logo"
        alt="classBOON"
        style={{ cursor: "pointer" }}
        onClick={() => navigate("/classsignoutpage")}
      />

      {/* Desktop: centered tab row + Exit */}
      <div className="cn-tabs">
        {links.map((l) => (
          <NavLink key={l.to} to={l.to} className={tabClass}>{l.label}</NavLink>
        ))}
      </div>
      <button className="cn-exit" onClick={() => setShowExit(true)} title="Exit Classroom Mode">
        🔒 Exit
      </button>

      {/* Mobile: hamburger → tiny dropdown with the same links + Exit */}
      <button
        className="cn-hamburger"
        aria-label={menuOpen ? "Close menu" : "Open menu"}
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen((o) => !o)}
      >
        <HamburgerIcon />
      </button>
      {menuOpen && (
        <>
          <div className="cn-menu-backdrop" onClick={closeMenu} />
          <div className="cn-menu">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} className={tabClass} onClick={closeMenu}>{l.label}</NavLink>
            ))}
            <button className="cn-menu-exit" onClick={openExit}>🔒 Exit</button>
          </div>
        </>
      )}

      {showExit && (
        <div className="cn-modal-overlay" onClick={closeModal}>
          <div className="cn-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Exit Classroom Mode</h2>

            {!forgot && (
              <>
                <p style={{ color: "var(--color-secondary)", fontSize: 14, margin: "0 0 4px" }}>
                  {classroomPin ? "Enter the 4-digit PIN to return to the full app." : "No PIN set — tap Exit to continue."}
                </p>
                {classroomPin && (
                  <input
                    className="cn-pin-input"
                    type="password"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={4}
                    value={pin}
                    autoFocus
                    onChange={(e) => { setPin(e.target.value.replace(/\D/g, "")); setError(""); }}
                    onKeyDown={(e) => { if (e.key === "Enter") tryExit(); }}
                    placeholder="••••"
                  />
                )}
                {error && <p className="cn-modal-error">{error}</p>}
                <div className="cn-modal-buttons">
                  <button onClick={closeModal} style={{ background: "#fff", color: "#333", border: "1px solid #ccc" }}>
                    Cancel
                  </button>
                  <button onClick={tryExit} disabled={!!classroomPin && pin.length < 4}>
                    Exit
                  </button>
                </div>
                {classroomPin && (
                  <button
                    type="button"
                    onClick={() => { setForgot(true); setError(""); setPin(""); }}
                    style={{ background: "none", border: "none", color: "var(--color-primary)", fontSize: 13, textDecoration: "underline", cursor: "pointer", marginTop: 10, padding: 0 }}
                  >
                    Forgot PIN?
                  </button>
                )}
              </>
            )}

            {forgot && (
              <>
                <p style={{ color: "var(--color-secondary)", fontSize: 14, margin: "0 0 8px" }}>
                  {hasPassword
                    ? "Enter your account password to exit and reset your PIN in Settings."
                    : "Verify your Google account to exit and reset your PIN in Settings."}
                </p>
                {hasPassword && (
                  <input
                    className="cn-pin-input"
                    type="password"
                    value={pw}
                    autoFocus
                    style={{ letterSpacing: "normal", fontSize: 16 }}
                    onChange={(e) => { setPw(e.target.value); setError(""); }}
                    onKeyDown={(e) => { if (e.key === "Enter") recoverAndExit(); }}
                    placeholder="Account password"
                  />
                )}
                {error && <p className="cn-modal-error">{error}</p>}
                <div className="cn-modal-buttons">
                  <button onClick={() => { setForgot(false); setError(""); setPw(""); }} style={{ background: "#fff", color: "#333", border: "1px solid #ccc" }}>
                    Back
                  </button>
                  <button onClick={recoverAndExit} disabled={busy || (hasPassword && !pw)}>
                    {busy ? "Verifying…" : hasPassword ? "Verify & exit" : "Verify with Google"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
